import CancelIcon from '@mui/icons-material/Cancel';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import trLocale from 'date-fns/locale/tr';
import moment from 'moment';
import * as XLSX from 'xlsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import JsonKeyValueViewer from 'src/components/JsonKeyValueViewer';
import {
  getAutomationDictionary,
  getAutomationLogList,
  getAutomationLogSummary,
  getAutomationTypes,
  simulateAutomationCase
} from 'src/store/slices/automationLogSlice';

const C = {
  bg: '#F5F7F9',
  surface: '#FFFFFF',
  surface2: '#EEF1F4',
  surface3: '#E4E9ED',
  border: '#DCE2E7',
  borderStrong: '#C4CDD3',
  text: '#16202B',
  textMuted: '#5C6B78',
  textFaint: '#8A97A2',
  accent: '#0B7285',
  accentHover: '#095A68',
  accentTint: '#E3F1F3',
  success: '#1E8E5A',
  successTint: '#E4F6EC',
  error: '#C6362E',
  errorTint: '#FBEAE8',
  warning: '#B8720B',
  warningTint: '#FCF1DD',
  info: '#4A5FD1',
  infoTint: '#EAECFA',
  neutral: '#6B7684',
  neutralTint: '#EBEDEF'
};

// Kurumsal font politikasi geregi monospace aileleri (Consolas / Courier New /
// monospace vb.) kullanilamiyor. Sayilarin alt alta hizali durmasi icin mono
// font yerine Roboto + fontVariantNumeric: 'tabular-nums' kullaniliyor.
const FONT_NUM = 'Roboto, "Segoe UI", Arial, sans-serif';

const roundedField = { '& .MuiOutlinedInput-root': { borderRadius: '10px' } };

// Coklu secimde chip'ler alt satira kayip alani buyutmesin diye tek satirda tutuluyor;
// boylece secim yapildikca filtre satirinin hizasi bozulmuyor.
const multiSelectField = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    flexWrap: 'nowrap',
    overflow: 'hidden'
  }
};
const filterChipProps = { sx: { borderRadius: '8px', maxWidth: 120 } };

// Bos secim zaten "tum kayitlar" anlamina geliyor (backend o kosulu WHERE'e hic eklemiyor).
// 'Tümü' secenegi bunu gorunur kilar: secilince liste temizlenir, hicbir sey secili
// degilken de isaretli gorunur.
const ALL_OPTION = { code: '__ALL__', label: 'Tümü' };

// Sayisal MODEL_KODU tasiyan alanlar (backend'deki ModelKoduFields ile ayni olmali).
const MODEL_KODU_FIELDS = ['MODEL_KODU_GARAN'];

function withAllOption(options) {
  return [ALL_OPTION].concat(options);
}

function selectedOptions(options, codes) {
  if (codes.length === 0) return [ALL_OPTION];
  return options.filter((o) => codes.indexOf(o.code) >= 0);
}

function nextSelection(newValue, details) {
  if (details && details.option && details.option.code === ALL_OPTION.code) return [];
  return newValue.filter((v) => v.code !== ALL_OPTION.code).map((v) => v.code);
}

// Coklu secim listelerinde secili satir checkbox ile isaretlenir.
function renderCheckboxOption(props, option, { selected }) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box component="li" {...props} key={option.code} sx={{ fontSize: 12.5 }}>
      <Checkbox
        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
        checkedIcon={<CheckBoxIcon fontSize="small" />}
        checked={selected}
        size="small"
        sx={{ mr: 1, p: 0.5, color: C.borderStrong, '&.Mui-checked': { color: C.accent } }}
      />
      {option.label}
    </Box>
  );
}

// Fiyat alanlari otomasyonlarda tutarsiz tipte geliyor: kimi sayi (18599.0),
// kimi duz string ("18599"), kimi de Turkce ondalikli string ("30319,00").
// Hepsini tek bicime cevirir; cozulemezse deger oldugu gibi birakilir.
function formatMoney(value) {
  if (value === null || value === undefined || value === '') return value;

  let num = value;
  if (typeof num === 'string') {
    const raw = num.trim();
    const normalized = raw.indexOf(',') >= 0
      ? raw.replace(/\./g, '').replace(',', '.') // "30.319,00" / "30319,00"
      : raw;
    num = Number(normalized);
  }
  if (typeof num !== 'number' || Number.isNaN(num)) return value;

  return `${num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
}

function resultMeta(result) {
  switch (result) {
    case 'BASARILI': return { color: C.success, tint: C.successTint };
    case 'MUTABAKAT': return { color: C.info, tint: C.infoTint };
    case 'ARA_DURUM':
    case 'MUSTERI_ONAY_BEKLENIYOR': return { color: C.warning, tint: C.warningTint };
    case 'BASARISIZ':
    case 'HATA': return { color: C.error, tint: C.errorTint };
    case 'ATLANDI': return { color: C.neutral, tint: C.neutralTint };
    default: return { color: C.neutral, tint: C.neutralTint };
  }
}

function ResultChip({ result, label }) {
  const meta = resultMeta(result);
  return (
    <Chip
      size="small"
      label={label || result}
      icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: meta.color, ml: '8px' }} />}
      sx={{
        bgcolor: meta.tint,
        color: meta.color,
        fontSize: 11,
        fontWeight: 700,
        '& .MuiChip-icon': { color: meta.color }
      }}
    />
  );
}

// Deger degistiginde once 0'dan baslatip bir tick sonra hedefe cekerek CSS transition'i tetikler.
// prefers-reduced-motion acikken animasyon atlanip dogrudan son deger gosterilir.
function useFillAnimation(key) {
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setFilled(true);
      return undefined;
    }
    setFilled(false);
    const id = window.setTimeout(() => setFilled(true), 60);
    return () => window.clearTimeout(id);
  }, [key]);

  return filled;
}

// Yarim daire (semi-circle) gauge - dolum animasyonu stroke-dashoffset uzerinden yapiliyor.
// pathLength=100 sayesinde dasharray/dashoffset dogrudan yuzde degeri olarak kullanilabiliyor.
function SemiGauge({ pct, color, trackColor, value, suffix }) {
  const target = Math.max(0, Math.min(100, pct));
  const filled = useFillAnimation(target);
  const fill = filled ? target : 0;

  return (
    <Box sx={{ position: 'relative', width: 124, height: 64, mx: 'auto' }}>
      <Box component="svg" viewBox="0 0 132 72" sx={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}>
        <path
          d="M 14 62 A 52 52 0 0 1 118 62"
          fill="none"
          stroke={trackColor}
          strokeWidth="10"
          strokeLinecap="round"
        />
        <path
          d="M 14 62 A 52 52 0 0 1 118 62"
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          pathLength="100"
          strokeDasharray="100"
          strokeDashoffset={100 - fill}
          style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(.22,.75,.3,1)' }}
        />
      </Box>
      <Stack
        alignItems="center"
        sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}
      >
        <Typography sx={{ fontSize: 23, fontWeight: 600, color: C.text, lineHeight: 1.05 }}>
          {value}
        </Typography>
        {suffix && <Typography sx={{ fontSize: 11.5, color: C.textMuted }}>{suffix}</Typography>}
      </Stack>
    </Box>
  );
}

function StatTile({ label, value, suffix, color, trackColor, footer, barPct }) {
  return (
    <Card
      variant="outlined"
      sx={{ borderTop: `3px solid ${color}`, borderColor: C.border, borderRadius: '8px', p: '10px 14px 8px', bgcolor: C.surface, height: '100%' }}
    >
      <Stack spacing={0.5} alignItems="center">
        <Typography sx={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.textFaint, fontWeight: 700, textAlign: 'center' }}>
          {label}
        </Typography>
        <SemiGauge pct={barPct} color={color} trackColor={trackColor} value={value} suffix={suffix} />
        <Typography sx={{ fontSize: 11.5, color: C.textFaint, textAlign: 'center' }}>{footer}</Typography>
      </Stack>
    </Card>
  );
}

function DistributionRow({ automationType, totalForType, segments, automationTypeMap, resultText, isLast }) {
  const filled = useFillAnimation(segments.map((s) => `${s.result}:${s.count}`).join('|'));

  return (
    <Box sx={{ py: 1.5, borderBottom: isLast ? 'none' : `1px solid ${C.border}` }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          {automationTypeMap[automationType] || '—'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: C.textFaint, fontVariantNumeric: 'tabular-nums' }}>
          {totalForType.toLocaleString('tr-TR')} kayıt
        </Typography>
      </Stack>

      <Box sx={{ display: 'flex', height: 26, borderRadius: '6px', overflow: 'hidden', bgcolor: C.surface2 }}>
        {segments.map((s, i) => (
          <Box
            key={s.result}
            title={`${resultText(s.result)}: ${s.count.toLocaleString('tr-TR')} (%${Math.round(s.pct)})`}
            sx={{
              width: filled ? `${s.pct}%` : 0,
              bgcolor: resultMeta(s.result).color,
              boxSizing: 'border-box',
              borderRight: i === segments.length - 1 ? 'none' : `2px solid ${C.surface}`,
              transition: `width ${600 + i * 120}ms cubic-bezier(.22,.75,.3,1)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}
          >
            {/* Etiket ancak sigacak kadar genis dilimlerde basilir; dolgu uzerindeki
                metin kontrast icin beyaz kalir. */}
            {s.pct >= 10 && (
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                %{Math.round(s.pct)}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function todayStr() {
  return moment().format('YYYY-MM-DD');
}

// Kayitlari xlsx olarak indirir. CSV yerine xlsx: Excel'de ayrac/kodlama sorunu cikmiyor.
function downloadXlsx(fileName, sheetName, jsonRows, columnWidths) {
  const worksheet = XLSX.utils.json_to_sheet(jsonRows);
  worksheet['!cols'] = columnWidths.map((w) => ({ wch: w }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, fileName);
}

function emptyFilters() {
  return {
    AutomationTypes: [],
    Results: [],
    ReasonCodes: [],
    ReceiptNumber: '',
    StartDate: todayStr(),
    EndDate: todayStr()
  };
}

function buildRequest(filters) {
  return {
    AutomationTypes: filters.AutomationTypes.map((t) => Number(t)),
    Results: filters.Results,
    ReasonCodes: filters.ReasonCodes,
    UnitCode: null,
    ApplicationNumber: null,
    ReceiptNumber: filters.ReceiptNumber ? Number(filters.ReceiptNumber) : null,
    StartDate: filters.StartDate || null,
    EndDate: filters.EndDate || null
  };
}

function AutomationLogs() {
  const dispatch = useDispatch();
  const automationTypesRaw = useSelector((state) => state.automationLogSlice.automationTypes) || [];
  const dictionary = useSelector((state) => state.automationLogSlice.dictionary) || [];
  const automationTypeMap = useMemo(() => {
    const map = {};
    automationTypesRaw.forEach((t) => { map[t.AutomationType] = t.Description; });
    return map;
  }, [automationTypesRaw]);

  // Sozluk tablosunu (T_YAPI_URUN_DEGISIM_OTOMASYON_SOZLUK) hizli erisim icin indeksle.
  // Anahtar formati: TIP|OTOMASYON_TIP|ANAHTAR|DEGER  - otomasyona ozel kayit yoksa
  // OTOMASYON_TIP=* (tablodaki NULL) satirina dusulur.
  const dict = useMemo(() => {
    const map = {};
    dictionary.forEach((d) => {
      const oto = d.AutomationType === null || d.AutomationType === undefined ? '*' : d.AutomationType;
      map[`${d.Type}|${oto}|${d.Key}|${d.Value === null || d.Value === undefined ? '' : d.Value}`] = d;
    });
    const find = (type, automationType, key, value) => map[`${type}|${automationType}|${key}|${value ?? ''}`]
      || map[`${type}|*|${key}|${value ?? ''}`];

    // Otomasyon secili degilken sebep kodu metnini bulmak icin (kod -> ilk eslesen aciklama).
    // Ayni kod birden fazla otomasyonda varsa ilki alinir; filtre listesinde bu yeterli.
    const anyReason = {};
    dictionary.forEach((d) => {
      if (d.Type === 'SEBEP_KODU' && !anyReason[d.Key]) anyReason[d.Key] = d.Description;
    });

    return {
      reasonTextAny: (code) => anyReason[code] || code,
      // SONUC / SEBEP_KODU metni; karsiligi yoksa ham kod gosterilir
      resultText: (code) => find('SONUC', '*', code)?.Description || code,
      reasonText: (automationType, code) => find('SEBEP_KODU', automationType, code)?.Description || code,
      fieldLabel: (automationType, key) => find('ALAN_ETIKET', automationType, key)?.Description || key,
      fieldHidden: (automationType, key) => find('ALAN_ETIKET', automationType, key)?.Show === false,
      fieldOrder: (automationType, key) => find('ALAN_ETIKET', automationType, key)?.Order ?? 9999,
      // TIP='ALAN_BICIM', ACIKLAMA='PARA' olan alanlar para birimi olarak bicimlenir
      fieldFormat: (automationType, key) => find('ALAN_BICIM', automationType, key)?.Description || null,
      // TIP='ALAN_BOS': SP'nin FOR JSON'u NULL degerlerde anahtari hic yazmiyor.
      // Bu alanlar icin yokluk anlamli oldugundan (orn. "kosul saglanmadi"), satir
      // yine de basilir ve buradaki metin gosterilir.
      // Filtre secenekleri sozlukten beslenir; koda gomulu liste yok.
      // Sonuc satirlari SIRA'ya gore (surec sirasi), sebep kodlari otomasyon bazinda.
      resultOptions: () => dictionary
        .filter((d) => d.Type === 'SONUC')
        .slice()
        .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
        .map((d) => ({ code: d.Key, label: d.Description })),
      // '(BOS)' gercek bir SEBEP_KODU degil, NULL satirlar icin kullanilan sentinel;
      // filtreye konursa hicbir kayit donmeyecegi icin listeden cikariliyor.
      reasonOptions: (automationTypes) => {
        const seen = {};
        return dictionary
          .filter((d) => d.Type === 'SEBEP_KODU' && d.Key !== '(BOS)')
          .filter((d) => automationTypes.length === 0 || automationTypes.indexOf(String(d.AutomationType)) >= 0)
          .filter((d) => {
            if (seen[d.Key]) return false;
            seen[d.Key] = true;
            return true;
          })
          .map((d) => ({ code: d.Key, label: d.Description }));
      },
      missingFields: (automationType) => dictionary.filter(
        (d) => d.Type === 'ALAN_BOS'
          && (d.AutomationType === automationType || d.AutomationType === null || d.AutomationType === undefined)
      ),
      fieldValue: (automationType, key, value) => {
        if (value === null || value === undefined || typeof value === 'object') return value;
        return find('ALAN_DEGER', automationType, key, String(value))?.Description ?? value;
      }
    };
  }, [dictionary]);

  const [filters, setFilters] = useState(emptyFilters());
  const [rows, setRows] = useState([]);
  const [modelDescriptions, setModelDescriptions] = useState({});
  const [modelKoduMap, setModelKoduMap] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);

  const search = (nextFilters) => {
    setLoading(true);
    setPage(0);
    const request = buildRequest(nextFilters);
    dispatch(getAutomationLogList(request)).then((response) => {
      setRows((response.payload?.Rows || []).map((r) => ({ ...r, id: r.Id })));
      setModelDescriptions(response.payload?.ModelDescriptions || {});
      setModelKoduMap(response.payload?.ModelKoduMap || {});
      setTotalCount(response.payload?.TotalCount || 0);
      setLoading(false);
    });
    dispatch(getAutomationLogSummary(request)).then((response) => {
      setSummary(response.payload || []);
    });
  };

  useEffect(() => {
    search(filters);
    dispatch(getAutomationTypes());
    dispatch(getAutomationDictionary());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    search(filters);
  };

  const handleClearClick = () => {
    const cleared = emptyFilters();
    setFilters(cleared);
    search(cleared);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setPage(0);
  };

  const openDetail = (row) => {
    setSelectedRow(row);
    setSimulation(null);
  };

  // Ekrandaki filtreye uyan TUM kayitlar disari aktarilir (sadece gorunen sayfa degil),
  // kodlar yerine kullaniciya gosterilen karsiliklariyla.
  const handleExport = () => {
    const jsonRows = rows.map((r) => ({
      Tarih: moment(r.WorkDate).format('DD.MM.YYYY HH:mm:ss'),
      Otomasyon: automationTypeMap[r.AutomationType] || '',
      'Fiş No': r.ReceiptNumber ?? '',
      Sonuç: dict.resultText(r.Result),
      Sebep: dict.reasonText(r.AutomationType, r.ReasonCode || '(BOS)')
    }));
    downloadXlsx(
      `Otomatik Onay Surec Takip ${moment().format('YYYYMMDD-HHmm')}.xlsx`,
      'Log Kayitlari',
      jsonRows,
      [20, 30, 14, 26, 55]
    );
  };

  const runSimulation = () => {
    if (!selectedRow) return;
    setSimulationLoading(true);
    dispatch(simulateAutomationCase({ UnitCode: selectedRow.UnitCode, Variables: selectedRow.Variables, WorkDate: selectedRow.WorkDate })).then((response) => {
      setSimulation(response.payload);
      setSimulationLoading(false);
    });
  };

  const stats = useMemo(() => {
    let total = 0;
    let success = 0;
    let reconciliation = 0;
    let spFail = 0;
    let pending = 0;
    let skipped = 0;
    summary.forEach((s) => {
      total += s.Count;
      if (s.Result === 'BASARILI') success += s.Count;
      if (s.Result === 'MUTABAKAT') reconciliation += s.Count;
      if (s.AutomationType === 1 && s.Result === 'BASARISIZ') spFail += s.Count;
      if (s.Result === 'ARA_DURUM' || s.Result === 'MUSTERI_ONAY_BEKLENIYOR') pending += s.Count;
      if (s.Result === 'ATLANDI') skipped += s.Count;
    });
    return { total, success, reconciliation, spFail, pending, skipped };
  }, [summary]);

  const distribution = useMemo(() => {
    const groups = {};
    summary.forEach((s) => {
      if (!groups[s.AutomationType]) groups[s.AutomationType] = {};
      groups[s.AutomationType][s.Result] = (groups[s.AutomationType][s.Result] || 0) + s.Count;
    });
    return Object.keys(groups).map((type) => {
      const resultCounts = groups[type];
      const totalForType = Object.values(resultCounts).reduce((a, b) => a + b, 0);
      const segments = Object.entries(resultCounts)
        .map(([result, count]) => ({ result, count, pct: totalForType ? (count / totalForType) * 100 : 0 }))
        .sort((a, b) => b.count - a.count);
      return { automationType: Number(type), totalForType, segments };
    }).sort((a, b) => a.automationType - b.automationType);
  }, [summary]);

  const pct = (n) => (stats.total ? Math.round((n / stats.total) * 100) : 0);

  // DEGISKENLER'i ayristirip, degeri olmayan ama yoklugu anlamli olan alanlari
  // (ALAN_BOS) sozlukteki metinle tamamlar. "Deger yok" iki sekilde gelebilir:
  // anahtar hic yazilmamis (FOR JSON varsayilani) ya da acikca null (INCLUDE_NULL_VALUES);
  // ikisi de ayni anlama geldiginden ayni metinle karsilanir.
  // 'Evet'/'Hayir' metinleri boolean'a cevrilir ki diger evet/hayir alanlariyla
  // ayni sekilde renklensin.
  const buildVariables = (row) => {
    let parsed;
    try {
      parsed = row.Variables ? JSON.parse(row.Variables) : {};
    } catch (e) {
      return {};
    }
    dict.missingFields(row.AutomationType).forEach((d) => {
      const current = parsed[d.Key];
      if (current !== null && current !== undefined) return;
      if (d.Description === 'Evet') parsed[d.Key] = true;
      else if (d.Description === 'Hayır') parsed[d.Key] = false;
      else parsed[d.Key] = d.Description;
    });
    return parsed;
  };

  // Model kodlarinin yanina T_YAPI_MODEL aciklamasini ekler.
  // Backend sadece bu sonuc kumesinde gecen model kodlarini gonderdiginden,
  // haritada karsiligi olan degerler model kodu kabul edilir.
  const describeModels = (value) => {
    if (Array.isArray(value)) {
      // Aday listeleri kart olarak basildigindan model aciklamasi Kod'a eklenmez,
      // ayri 'Aciklama' alani olarak verilir (kartta alt baslik olarak gosterilir)
      return value.map((item) => {
        if (!item || typeof item !== 'object') return item;
        const next = { ...item };
        if (next.Kod) {
          const desc = modelDescriptions[String(next.Kod)];
          if (desc) next.Aciklama = desc;
        }
        if (next.Fiyat !== null && next.Fiyat !== undefined) next.Fiyat = formatMoney(next.Fiyat);
        return next;
      });
    }
    if (value === null || value === undefined || typeof value === 'object') return value;
    const desc = modelDescriptions[String(value)];
    return desc ? `${value} — ${desc}` : value;
  };

  // Tip 1 loglari modeli sayisal MODEL_KODU olarak tutar; kullaniciya KUL_MODEL_KODU gosterilir.
  // Esleme sadece bu alanlara uygulanir, diger sayisal alanlar model koduyla karistirilmaz.
  const displayModelValue = (key, value) => {
    if (MODEL_KODU_FIELDS.includes(key) && value !== null && value !== undefined) {
      return describeModels(modelKoduMap[String(value)] ?? value);
    }
    return describeModels(value);
  };

  // Simulasyon kosullarinin altindaki degisken satirlari. Islem Bilgileri panelinin
  // kullandigi sozluk zinciri (ALAN_ETIKET / ALAN_DEGER / ALAN_BICIM) burada da
  // aynen uygulanir; backend ham deger gonderir, ceviri tek yerden yapilir.
  const conditionFieldText = (automationType, key, value) => {
    if (value === null || value === undefined || value === '') return 'Bilgi yok';
    const resolved = dict.fieldValue(automationType, key, value);
    if (typeof resolved === 'boolean') return resolved ? 'Evet' : 'Hayır';
    if (dict.fieldFormat(automationType, key) === 'PARA') return formatMoney(resolved);
    return displayModelValue(key, resolved);
  };

  const pagedRows = useMemo(
    () => rows.slice(page * pageSize, (page + 1) * pageSize),
    [rows, page, pageSize]
  );

  const automationTypeOptions = useMemo(
    () => automationTypesRaw.map((t) => ({ code: String(t.AutomationType), label: t.Description })),
    [automationTypesRaw]
  );
  const resultOptions = useMemo(() => dict.resultOptions(), [dict]);
  // Legend yalnizca veride gercekten gecen sonuclari gosterir, sozluk sirasiyla.
  const legendResults = useMemo(() => {
    const present = {};
    summary.forEach((s) => { present[s.Result] = true; });
    return dict.resultOptions().filter((o) => present[o.code]);
  }, [summary, dict]);
  // Otomasyon secildiyse sebep listesi ona gore daralir
  const reasonOptions = useMemo(
    () => dict.reasonOptions(filters.AutomationTypes),
    [dict, filters.AutomationTypes]
  );

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', p: '10px 28px 32px', fontFamily: '"Segoe UI", "Segoe UI Variable", -apple-system, "Helvetica Neue", ui-sans-serif, sans-serif' }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 600, color: C.text, letterSpacing: '-0.01em' }}>
          Otomatik Onay Süreç Takip
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: C.textMuted, mt: 0.25 }}>
          Otomatik Onay A/B/C/D · SAP Gönderim · Ücretli Değişim otomasyonlarının onay kararları tek ekrandan izlenir
        </Typography>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={2}>
          <StatTile label="Toplam Kayıt" value={stats.total.toLocaleString('tr-TR')} color={C.accent} trackColor={C.accentTint} barPct={100} footer="Seçili filtre/aralık" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatTile label="Başarılı" value={stats.success.toLocaleString('tr-TR')} suffix={`%${pct(stats.success)}`} color={C.success} trackColor={C.successTint} barPct={pct(stats.success)} footer="Kullanıcı müdahalesi gerekmedi" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatTile label="Çözüm Merkezi Mutabakatı" value={stats.reconciliation.toLocaleString('tr-TR')} suffix={`%${pct(stats.reconciliation)}`} color={C.info} trackColor={C.infoTint} barPct={pct(stats.reconciliation)} footer="Çözüm Merkezi - Mutabakat'a alındı" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatTile label="Otomatik Onay Alamadı" value={stats.spFail.toLocaleString('tr-TR')} suffix={`%${pct(stats.spFail)}`} color={C.error} trackColor={C.errorTint} barPct={pct(stats.spFail)} footer={`${automationTypeMap[1] || 'Otomatik Onay A/B/C/D'} · hiçbir koşul sağlanmadı`} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatTile label="Muadil Belirleme / Onay Bekliyor" value={stats.pending.toLocaleString('tr-TR')} suffix={`%${pct(stats.pending)}`} color={C.warning} trackColor={C.warningTint} barPct={pct(stats.pending)} footer="SAP Gönderim · Ücretli Değişim" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatTile label="Giriş Koşulu ile Atlandı" value={stats.skipped.toLocaleString('tr-TR')} suffix={`%${pct(stats.skipped)}`} color={C.neutral} trackColor={C.neutralTint} barPct={pct(stats.skipped)} footer={`${automationTypeMap[3] || 'Ücretli Değişim'} · otomasyona hiç girmedi`} />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ borderColor: C.border, borderRadius: '8px', mb: 2, bgcolor: C.surface }}>
        <Box sx={{ px: 2.25, py: 1.75, borderBottom: `1px solid ${C.border}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>Otomasyon başına sonuç dağılımı</Typography>
        </Box>
        <Box sx={{ px: 2.25, py: 0.5, minHeight: 168, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {loading && (
            <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
              <CircularProgress size={22} sx={{ color: C.accent }} />
            </Stack>
          )}
          {!loading && distribution.length === 0 && (
            <Typography sx={{ fontSize: 12.5, color: C.textFaint }}>Veri yok</Typography>
          )}
          {!loading && distribution.map((d, i) => (
            <DistributionRow
              key={d.automationType}
              {...d}
              automationTypeMap={automationTypeMap}
              resultText={dict.resultText}
              isLast={i === distribution.length - 1}
            />
          ))}
        </Box>

        {!loading && legendResults.length > 0 && (
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={2}
            sx={{ px: 2.25, py: 1.25, borderTop: `1px solid ${C.border}`, bgcolor: C.surface2 }}
          >
            {legendResults.map((r) => (
              <Stack key={r.code} direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: resultMeta(r.code).color }} />
                <Typography sx={{ fontSize: 11.5, color: C.textMuted }}>{r.label}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Card>

      <Card variant="outlined" sx={{ borderColor: C.border, borderRadius: '8px', mb: 2, bgcolor: C.surface, overflow: 'visible' }}>
        <Box sx={{ px: 2.25, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>Filtrele</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'minmax(320px, 1.7fr) minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 1.2fr) minmax(0, 0.9fr) auto'
              },
              gap: 1.5,
              alignItems: 'center'
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns} locale={trLocale}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DatePicker
                  label="Başlangıç"
                  format="dd.MM.yyyy"
                  value={filters.StartDate ? moment(filters.StartDate, 'YYYY-MM-DD').toDate() : null}
                  onChange={(newValue) => setFilters({ ...filters, StartDate: newValue ? moment(newValue).format('YYYY-MM-DD') : '' })}
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField {...params} size="small" fullWidth sx={roundedField} />
                  )}
                />
                <Typography sx={{ color: C.textFaint }}>–</Typography>
                <DatePicker
                  label="Bitiş"
                  format="dd.MM.yyyy"
                  value={filters.EndDate ? moment(filters.EndDate, 'YYYY-MM-DD').toDate() : null}
                  onChange={(newValue) => setFilters({ ...filters, EndDate: newValue ? moment(newValue).format('YYYY-MM-DD') : '' })}
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField {...params} size="small" fullWidth sx={roundedField} />
                  )}
                />
              </Stack>
            </LocalizationProvider>

            <Autocomplete
              multiple
              size="small"
              fullWidth
              disableCloseOnSelect
              options={withAllOption(automationTypeOptions)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              value={selectedOptions(automationTypeOptions, filters.AutomationTypes)}
              onChange={(e, newValue, reason, details) => setFilters({
                ...filters,
                AutomationTypes: nextSelection(newValue, details),
                ReasonCodes: []
              })}
              limitTags={1}
              ChipProps={filterChipProps}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} label="Otomasyon" sx={multiSelectField} />
              )}
            />

            <Autocomplete
              multiple
              size="small"
              fullWidth
              disableCloseOnSelect
              options={withAllOption(resultOptions)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              value={selectedOptions(resultOptions, filters.Results)}
              onChange={(e, newValue, reason, details) => setFilters({ ...filters, Results: nextSelection(newValue, details) })}
              limitTags={1}
              ChipProps={filterChipProps}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} label="Sonuç" sx={multiSelectField} />
              )}
            />

            <Autocomplete
              multiple
              size="small"
              fullWidth
              disableCloseOnSelect
              options={withAllOption(reasonOptions)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              value={selectedOptions(reasonOptions, filters.ReasonCodes)}
              onChange={(e, newValue, reason, details) => setFilters({ ...filters, ReasonCodes: nextSelection(newValue, details) })}
              limitTags={1}
              ChipProps={filterChipProps}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <TextField {...params} label="Sebep" sx={multiSelectField} />
              )}
            />

            <TextField size="small" label="Fiş No" fullWidth sx={roundedField}
              value={filters.ReceiptNumber} onChange={(e) => setFilters({ ...filters, ReceiptNumber: e.target.value })} />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleClearClick}
                sx={{ borderColor: C.border, color: C.textMuted, textTransform: 'none', borderRadius: '10px' }}
              >
                Temizle
              </Button>
              <Button
                variant="contained"
                onClick={handleSearchClick}
                sx={{
                  bgcolor: C.accent,
                  '&:hover': { bgcolor: C.accentHover },
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '10px',
                  minWidth: 130
                }}
              >
                Ara
              </Button>
            </Stack>
          </Box>
        </Box>
      </Card>

      <Card variant="outlined" sx={{ borderColor: C.border, borderRadius: '8px', bgcolor: C.surface }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.25, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>Log Kayıtları</Typography>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography sx={{ fontSize: 12, color: C.textFaint }}>{totalCount.toLocaleString('tr-TR')} kayıt</Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={loading || rows.length === 0}
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={handleExport}
              sx={{
                borderColor: C.accent,
                color: C.accent,
                bgcolor: C.accentTint,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                '&:hover': { borderColor: C.accentHover, bgcolor: C.accent, color: '#fff' }
              }}
            >
              Excel&apos;e Aktar
            </Button>
          </Stack>
        </Stack>
        <TableContainer sx={{ height: 640 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 3, p: 0, bgcolor: C.surface2 }} />
                {/* Sabit genislikli/atomik icerik ortalanir; degisken uzunluktaki
                    metin kolonlari sola hizalanir ki sol kenar tirtikli gorunmesin. */}
                {[
                  { label: 'Tarih', width: 170, align: 'center' },
                  { label: 'Otomasyon', width: 230, align: 'left' },
                  { label: 'Fiş No', width: 150, align: 'center' },
                  { label: 'Sonuç', width: 210, align: 'left' },
                  { label: 'Sebep', align: 'left' },
                  { label: '', width: 110, align: 'center' }
                ].map((h) => (
                  <TableCell
                    key={h.label || 'actions'}
                    align={h.align}
                    sx={{ width: h.width, bgcolor: C.surface2, color: C.textFaint, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, whiteSpace: 'nowrap' }}
                  >
                    {h.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ height: 560, borderBottom: 'none' }}>
                    <CircularProgress size={22} sx={{ color: C.accent }} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && pagedRows.map((row) => {
                const meta = resultMeta(row.Result);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => openDetail(row)}
                    sx={{ cursor: 'pointer', '& td': { borderBottom: `1px solid ${C.border}`, fontSize: 13 } }}
                  >
                    <TableCell sx={{ width: 3, p: 0, bgcolor: meta.color }} />
                    <TableCell align="center" sx={{ color: C.textFaint, fontFamily: FONT_NUM, fontSize: 12, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {moment(row.WorkDate).format('DD.MM.YYYY HH:mm:ss')}
                    </TableCell>
                    <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>{automationTypeMap[row.AutomationType] || '—'}</TableCell>
                    <TableCell align="center" sx={{ fontFamily: FONT_NUM, fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{row.ReceiptNumber ?? '—'}</TableCell>
                    <TableCell align="left"><ResultChip result={row.Result} label={dict.resultText(row.Result)} /></TableCell>
                    <TableCell align="left" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                      title={dict.reasonText(row.AutomationType, row.ReasonCode || '(BOS)')}
                    >
                      {dict.reasonText(row.AutomationType, row.ReasonCode || '(BOS)')}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                        onClick={(e) => { e.stopPropagation(); openDetail(row); }}
                        sx={{
                          borderColor: C.accent,
                          color: C.accent,
                          bgcolor: C.accentTint,
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': { borderColor: C.accentHover, bgcolor: C.accent, color: '#fff' }
                        }}
                      >
                        Detay
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ height: 560, borderBottom: 'none', color: C.textFaint }}>
                    Kayıt bulunamadı
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Sayfa başına"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
          sx={{ color: C.textMuted }}
        />
      </Card>

      {/* TopBar zIndex.drawer + 1 ile sabitlendiginden (layouts/Page/TopBar.js) varsayilan
          drawer onun altinda kaliyordu; bir kademe yukari alindi. */}
      <Drawer
        anchor="right"
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      >
        <Box sx={{ width: { xs: '92vw', sm: 560, md: 640 }, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: C.surface }}>
          {selectedRow && (
            <>
              <Box sx={{ p: '16px 20px', borderBottom: `1px solid ${C.border}`, bgcolor: C.surface2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.textFaint, fontWeight: 700 }}>
                      {automationTypeMap[selectedRow.AutomationType] || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: C.text, mt: '2px' }}>
                      Fiş No{' '}
                      <Box component="span" sx={{ fontFamily: FONT_NUM, fontVariantNumeric: 'tabular-nums' }}>
                        {selectedRow.ReceiptNumber ?? '—'}
                      </Box>
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setSelectedRow(null)} sx={{ bgcolor: C.surface, border: `1px solid ${C.border}`, '&:hover': { bgcolor: C.surface3 } }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.5 }}>
                  <ResultChip result={selectedRow.Result} label={dict.resultText(selectedRow.Result)} />
                  <Typography sx={{ fontSize: 12, color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                    {moment(selectedRow.WorkDate).format('DD.MM.YYYY HH:mm:ss')}
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ p: '18px 20px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Box>
                  <Typography sx={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.textFaint, fontWeight: 700, mb: 1 }}>
                    Sonuç Bilgisi
                  </Typography>
                  <Box sx={{ border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
                    <Box sx={{ p: '10px 12px', borderLeft: `3px solid ${resultMeta(selectedRow.Result).color}` }}>
                      <Typography sx={{ fontSize: 13.5, color: C.text, fontWeight: 600 }}>
                        {dict.reasonText(selectedRow.AutomationType, selectedRow.ReasonCode || '(BOS)')}
                      </Typography>
                      {selectedRow.ReasonDescription && (
                        <Typography sx={{ fontSize: 12.5, color: C.textMuted, mt: 0.5 }}>
                          {selectedRow.ReasonDescription}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {selectedRow.ErrorMessage && <Alert severity="error">{selectedRow.ErrorMessage}</Alert>}

                {selectedRow.AutomationType === 1 && (
                  <Box>
                    <Typography sx={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.textFaint, fontWeight: 700, mb: 1 }}>
                      Case A / B / C / D Simülasyonu
                    </Typography>
                    <Button
                      fullWidth
                      onClick={runSimulation}
                      disabled={simulationLoading}
                      sx={{ border: `1px dashed ${C.borderStrong}`, bgcolor: C.surface2, color: C.textMuted, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: C.surface3, borderColor: C.accent, color: C.text } }}
                    >
                      {simulationLoading ? 'Hesaplanıyor…' : simulation ? 'Yeniden hesapla' : 'Koşulları hesapla'}
                    </Button>

                    {simulation && (
                      <Box sx={{ mt: 1.5 }}>
                        <Alert severity={simulation.AllMatchingCases?.length ? 'success' : 'warning'} sx={{ mb: 1 }}>
                          {simulation.AllMatchingCases?.length
                            ? `Sağlanan case(ler): ${simulation.AllMatchingCases.join(', ')}`
                            : 'Hiçbir case (A/B/C/D) tüm koşulları sağlamıyor.'}
                        </Alert>
                        {simulation.Cases?.map((c) => {
                          const passedCount = c.Conditions.filter((cond) => cond.Passed).length;
                          return (
                            <Accordion key={c.Case} disableGutters sx={{ border: `1px solid ${C.border}`, boxShadow: 'none', '&:before': { display: 'none' }, mb: 1, borderRadius: '6px', overflow: 'hidden' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%', pr: 1 }}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{
                                      width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: 11, color: '#fff', bgcolor: c.AllConditionsPassed ? C.success : C.textFaint
                                    }}
                                    >
                                      {c.Case}
                                    </Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Case {c.Case}</Typography>
                                  </Stack>
                                  <Typography sx={{ fontSize: 11, color: C.textFaint, fontFamily: FONT_NUM, fontVariantNumeric: 'tabular-nums' }}>
                                    {passedCount}/{c.Conditions.length} koşul
                                  </Typography>
                                </Stack>
                              </AccordionSummary>
                              <AccordionDetails sx={{ pt: 0 }}>
                                {c.Conditions.map((cond, i) => (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <Stack key={i} direction="row" spacing={1} sx={{ py: 0.75, borderTop: i === 0 ? 'none' : `1px solid ${C.border}` }}>
                                    {cond.Passed
                                      ? <CheckCircleIcon sx={{ fontSize: 15, color: C.success, mt: '1px' }} />
                                      : <CancelIcon sx={{ fontSize: 15, color: C.error, mt: '1px' }} />}
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography sx={{ fontSize: 12, color: cond.Passed ? C.text : C.textMuted }}>{cond.Description}</Typography>
                                      {cond.Fields?.map((f) => (
                                        <Typography key={f.Key} sx={{ fontSize: 11, color: C.textFaint }}>
                                          {dict.fieldLabel(selectedRow.AutomationType, f.Key)}
                                          {': '}
                                          <Box component="span" sx={{ color: C.textMuted }}>
                                            {conditionFieldText(selectedRow.AutomationType, f.Key, f.Value)}
                                          </Box>
                                        </Typography>
                                      ))}
                                      {cond.Note && (
                                        <Typography sx={{ fontSize: 11, color: C.textFaint, fontStyle: 'italic' }}>{cond.Note}</Typography>
                                      )}
                                    </Box>
                                  </Stack>
                                ))}
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}

                <Box>
                  <Typography sx={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.textFaint, fontWeight: 700, mb: 1 }}>
                    İşlem Bilgileri
                  </Typography>
                  <JsonKeyValueViewer
                    data={buildVariables(selectedRow)}
                    resolver={{
                      label: (key) => dict.fieldLabel(selectedRow.AutomationType, key),
                      hidden: (key) => dict.fieldHidden(selectedRow.AutomationType, key),
                      order: (key) => dict.fieldOrder(selectedRow.AutomationType, key),
                      // Aday listesi tablolarinin kolon basliklari da ayni sozlukten cozulur
                      columnLabel: (col) => dict.fieldLabel(selectedRow.AutomationType, col),
                      displayValue: (key, value) => {
                        const resolved = dict.fieldValue(selectedRow.AutomationType, key, value);
                        if (dict.fieldFormat(selectedRow.AutomationType, key) === 'PARA') return formatMoney(resolved);
                        return displayModelValue(key, resolved);
                      }
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

export default AutomationLogs;
